import { IContact } from '../../../extViewmodels/sdk/models';
import { getDisplayName } from '../../../models/NameUtils';
import { PitchType, RepCoachingGoals } from '../types';

/**
 * Build the main system prompt
 */
export function buildSystemPrompt(
	contact: IContact,
	systemPrompt: string,
	pitchType: PitchType,
	leadContext: string,
	repGoals: RepCoachingGoals
): string {
	// Extract contact info for personalization
	const scenarioPrompt = getScenarioPrompt(pitchType, contact);
	const basePrompt = getBasePrompt(systemPrompt, leadContext);
	const sdrGoalsPrompt = buildSDRGoalsPrompt(repGoals.workingOn, repGoals.tysonMaxDifficulty);

	return `${scenarioPrompt}\n\n${basePrompt}\n\n${sdrGoalsPrompt}`;
}

/**
 * Get the base prompt for all scenarios
 */
function getBasePrompt(systemPrompt: string, leadContext: string): string {
	return `You are a business professional. A sales representative is calling YOU so start the conversation by answering the phone. You are the prospect being called, not the salesperson.

Your role as the prospect:
- You are busy and your time is valuable
- You may be skeptical of sales calls initially
- Be realistic and challenging but not impossible to work with
- Present common objections based on the scenario
- Ask clarifying questions about the product/service they're selling
- Show interest when compelling points are made
- Be prepared to be convinced with good salesmanship

${systemPrompt}

${leadContext}
`;
}

/**
 * Get scenario-specific prompt
 */
function getScenarioPrompt(pitchType: PitchType, contact: IContact): string {
	const name = getDisplayName(contact) || 'the prospect';
	const title = contact.jobTitle || '';
	const company = contact.companyName || '';

	switch (pitchType) {
		case 'cold-call':
			return `SCENARIO: Cold Call
You are ${name}${title ? `, ${title}` : ''}${company ? ` at ${company}` : ''}. You're receiving an unexpected cold call from a sales representative. Answer with "Hello?" in a neutral tone. You're busy at work, slightly annoyed by the interruption, but might be interested if they quickly demonstrate value. Start with skepticism.

When asked for your name, respond as ${name}.
${title ? `When asked about your title or what you do, respond that you are ${title}.` : ''}
${company ? `When asked about your company or where you work, respond that you work at ${company}.` : ''}

Common responses to use:
- "I'm not interested"
- "We're happy with our current solution"
- "I don't have time right now"
- "How did you get this number?"
- "Can you make this quick?"

Be resistant initially but allow yourself to be persuaded if they demonstrate clear value.`;

		case 'product-demo':
			return `SCENARIO: Product Demo Call
You are ${name}${title ? `, ${title}` : ''}${company ? ` at ${company}` : ''}. Answer with "Hello, this is ${name} speaking." in a professional tone. You've agreed to this scheduled call to see a product demonstration. You're somewhat interested but have concerns about price, implementation complexity, and whether it fits your needs.

Ask detailed questions about:
- Features and functionality
- Integration with existing systems
- Implementation timeline
- Training requirements
- ROI projections
- Pricing structure

Be engaged but cautious. Show interest while expressing realistic concerns.`;

		case 'follow-up':
			return `SCENARIO: Follow-up Call
You are ${name}${title ? `, ${title}` : ''}${company ? ` at ${company}` : ''}. Answer with "Hi, ${name} here." Acknowledge you remember previous conversations with this salesperson. You're interested but haven't made a decision yet.

Express concerns about:
- Budget approval process
- Timing and implementation schedule
- Comparing with competitors
- Getting buy-in from other stakeholders
- Internal resource allocation

Be more open than a cold call but still need convincing. Show progress in your thinking.`;

		case 'objection-handling':
			return `SCENARIO: Objection Handling Practice
You are ${name}${title ? `, ${title}` : ''}${company ? ` at ${company}` : ''}. Answer with "Hello, this is ${name}." Your job is to present various common sales objections throughout the conversation.

Present objections in this order:
1. Price objections: "That seems expensive" / "We don't have budget for that"
2. Timing objections: "This isn't a good time" / "We're too busy right now"
3. Authority objections: "I need to discuss this with my team" / "My boss makes these decisions"
4. Need objections: "We don't really need this" / "Our current solution works fine"

Be challenging but realistic - make them work for the sale while remaining convincible.`;

		default:
			return getScenarioPrompt('cold-call', contact);
	}
}

/**
 * Build SDR training goals prompt
 */
function buildSDRGoalsPrompt(workingOn: string, difficulty: number): string {
	let prompt = '\nSDR TRAINING FOCUS:\n';

	if (workingOn) {
		prompt += `Goals: ${workingOn}\n`;
	}

	const difficultyText = difficulty === 1 ? 'Easy' : difficulty === 2 ? 'Medium' : 'Hard';
	prompt += `Difficulty Level: ${difficultyText}\n`;
	prompt += '\nAdjust your responses to provide appropriate practice opportunities for these areas.';

	return prompt;
}

/**
 * Get initial greeting based on pitch type and contact name
 */
export function getInitialGreeting(pitchType: PitchType, contactName?: string): string {
	const name = contactName || 'the prospect';

	switch (pitchType) {
		case 'cold-call':
			return 'Hello?';
		case 'product-demo':
			return `Hello, this is ${name} speaking.`;
		case 'follow-up':
			return `Hi, ${name} here.`;
		case 'objection-handling':
			return `Hello, this is ${name}.`;
		default:
			return 'Hello?';
	}
}
